import type { Schema } from 'yup';

import { PurchasedFrom } from '../../constants/PurchasedFrom';
import type {
  AnyDocument,
  WorkflowStepFormFieldCustomValidationRule,
} from '../../types';
import { sanitizePrecision, stringToNumber } from '../general';

export const customRule = <T extends Schema>(
  name: WorkflowStepFormFieldCustomValidationRule,
  schema: T,
  rootDocument: AnyDocument | undefined,
): T => {
  switch (name) {
    case 'lessThanOrEqualToAssetAmount': {
      if (!rootDocument)
        throw new Error(
          'Cannot return root document: root document is missing.',
        );

      return schema.test(
        name,
        "This field must be less than or equal to the asset's value",
        (value) => {
          if ((rootDocument as any)?.asset?.value) {
            if (value) {
              const valueAsNumber = stringToNumber(value as string);
              return (
                sanitizePrecision(valueAsNumber * 100) <=
                ((rootDocument as any).asset.value as number)
              );
            }
          }

          return true;
        },
      );
    }

    case 'documentationFeeMax': {
      if (!rootDocument)
        throw new Error(
          'Cannot return root document: root document is missing.',
        );
      const purchasedFrom = (rootDocument as any)?.asset.purchasedFrom;
      const documentationFee = [
        PurchasedFrom.PrivateSeller,
        PurchasedFrom.Refinance,
      ].includes(purchasedFrom)
        ? 880
        : 990;

      const message = `Document fee must be less than $${documentationFee}.00`;

      return schema.test(name, message, (value) => {
        const valueAsNumber = stringToNumber(value as string);
        return valueAsNumber <= documentationFee;
      });
    }
    case 'documentationFeeMaxForConsumerLoan': {
      const maxFeeForConsumerLoanDocumentationFee = 990;

      const messageConsumerLoan = `Document fee must be less than $${maxFeeForConsumerLoanDocumentationFee}.00`;

      return schema.test(name, messageConsumerLoan, (value) => {
        const valueAsNumber = stringToNumber(value as string);
        return valueAsNumber <= maxFeeForConsumerLoanDocumentationFee;
      });
    }
    case 'customBrokerageRateMax':
    case 'brokerageRateMax': {
      if (!rootDocument)
        throw new Error(
          'Cannot return root document: root document is missing.',
        );
      return schema.when(
        name === 'brokerageRateMax'
          ? ['quote~assetValue', 'quote~deposit']
          : ['assetValue', 'deposit'],
        ([assetValue, deposit], extra) => {
          // Workout the NAF
          const netAmountFinanced =
            stringToNumber(assetValue ?? '0') -
            stringToNumber(deposit ?? '0') +
            sanitizePrecision((rootDocument?.quote.fees || 0) / 100);
          const brokerageRateMessage =
            netAmountFinanced > 50000
              ? 'Based on the current NAF, the max brokerage rate must be 8%'
              : 'Based on the current NAF, the max brokerage rate must be 11%';

          return extra.test(name, brokerageRateMessage, (value: string) => {
            if (!value) {
              return false;
            }
            const valueAsNumber = stringToNumber(value as string);
            return netAmountFinanced > 50000
              ? valueAsNumber <= 8
              : valueAsNumber <= 11;
          });
        },
      );
    }
    default:
  }
  return schema;
};
